// Tipos centralizados para filtros - Single source of truth

// Tipos para el intercambio de datos complejos entre componentes y FilterComponent
export interface FilterValueWithDisplay {
  id: string | number
  displayValue: string
}

// Tipo que representa el formato JSON que se envía entre componentes
export type FilterComplexValue = string // JSON serializado de FilterValueWithDisplay

// Funciones de transformación reutilizables
export const createFilterValueWithDisplay = (
  id: string | number,
  displayValue: string
): FilterComplexValue => {
  return JSON.stringify({ id, displayValue } as FilterValueWithDisplay)
}

export const parseFilterValueWithDisplay = (
  rawValue: FilterComplexValue
): FilterValueWithDisplay | null => {
  try {
    const parsed = JSON.parse(rawValue) as FilterValueWithDisplay
    // Validar que tenga las propiedades requeridas
    if (
      parsed &&
      typeof parsed === 'object' &&
      'id' in parsed &&
      'displayValue' in parsed
    ) {
      return parsed
    }
    return null
  } catch {
    return null
  }
}

// Transformadores genéricos reutilizables
export const createBackendTransformer = () => {
  return (rawValue: FilterComplexValue): string => {
    const parsed = parseFilterValueWithDisplay(rawValue)
    return parsed ? parsed.id.toString() : rawValue
  }
}

export const createDisplayTransformer = () => {
  return (rawValue: FilterComplexValue): string => {
    const parsed = parseFilterValueWithDisplay(rawValue)
    return parsed ? parsed.displayValue : rawValue
  }
}

export interface ComponentProps {
  value: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  fullWidth?: boolean
  variant?: 'outlined' | 'filled' | 'standard'
  // Transformadores opcionales para manejar valores complejos
  transformForBackend?: (rawValue: FilterComplexValue) => string // Extrae valor para backend (ej: ID)
  transformForDisplay?: (rawValue: FilterComplexValue) => string // Extrae valor para mostrar en UI
}

export interface FilterOption {
  order: number
  key: string
  keyfilter?: string[]
  label: string
  type:
    | 'numeric'
    | 'text'
    | 'range'
    | 'precreated'
    | 'custom'
    | 'date'
    | 'numericnotnegative'
    | 'textempty'
    | 'daterange'
    | 'yesornotoption'
  options?: Array<{ label: string; value: number | string }>
  Component?: React.ComponentType<ComponentProps> // Solo para 'custom'
  canBeRepeated?: boolean
  // Transformadores opcionales para componentes custom
  transformForBackend?: (rawValue: FilterComplexValue) => string // Extrae valor para backend (ej: ID)
  transformForDisplay?: (rawValue: FilterComplexValue) => string // Extrae valor para mostrar en UI
  // Otras propiedades que puedas necesitar para tipos específicos
  customOrder?: {
    // Función que devuelve el orden específico para un valor dado
    getOrder: (value: any) => number
    // O puede ser un objeto de mapeo { [value]: order }
    orderMap?: Record<string | number, number>
  }
  filterParams?: Record<string, any>
  // Límite de caracteres para 'text' / 'textempty'
  maxLength?: number
  // Configuración para 'range': define las dos keys de Filter que se generan
  // (desde/hasta) y sus labels. Permite reutilizar el mismo tipo 'range' en
  // cualquier página sin hardcodear el key del filtro dentro de FilterComponent.
  rangeConfig?: {
    initialKey: string
    finalKey: string
    initialLabel: string
    finalLabel: string
    inputType?: 'number' | 'text'
  }
}

export interface Filter {
  key: string
  keyfilter?: string[]
  label: string
  value: any
  displayValue: string
  canBeRepeated?: boolean
}

export interface FilterExtraAction {
  label: string
  onClick: () => void
  icon?: React.ReactNode
  tooltip?: string
  disabled?: boolean
}

export interface FilterComponentProps {
  options: FilterOption[]
  onSearchClick: (filters: Filter[]) => void
  selectedFilters: Filter[]
  setSelectedFilters: (value: React.SetStateAction<Filter[]>) => void
  includeAddButton?: boolean
  requiredFilterKeys?: string[]
  defaultSelectedFilterKey?: string
  includeSearchOnClean?: boolean
  onAddButtonClick?: () => void
  addButtonTitle?: string
  createButtonTooltip?: string
  extraActions?: FilterExtraAction[]
  // Nodo opcional que se renderiza al final de la barra de acciones (alineado a
  // la derecha). Reutilizable para agregar botones/íconos propios de cada pantalla.
  rightSlot?: React.ReactNode
}
